import { createAsyncThunk } from "@reduxjs/toolkit";
import BusinessApiService from "../apiServices/BusinessApiService";
import {
  BusinessMoreDetails,
  CreateBusinessPayload,
  SubmitRatingPayload,
  UpdateBusinessPayload,
} from "../../interfaces/BusinessObject";
import {
  CreateCollectionPayload,
  UpdateCollectionPayload,
} from "../../interfaces/BusinessCollectionObject";
import ProductApiService from "../apiServices/ProductApiService";
import { UserPreferences } from "../../interfaces/UserObject";

// Business search. Lets errors propagate so SearchPage's .unwrap() surfaces the
// real message (mirrors the profile-search thunk used by People search).
export const businessSearch = createAsyncThunk(
  "business/businessSearch",
  async (searchRq: { searchString: string; userPreferences: UserPreferences }) => {
    return await BusinessApiService.businessSearch(
      searchRq.searchString,
      searchRq.userPreferences
    );
  }
);

// Collections matching a name. Errors propagate so SearchPage's .unwrap() can
// show the real message (mirrors businessSearch).
export const collectionSearchByName = createAsyncThunk(
  "business/collectionSearchByName",
  async (searchRq: { collectionName: string }) => {
    return await BusinessApiService.searchCollectionsByName(
      searchRq.collectionName
    );
  }
);

// One collection + its members in a single call. Errors propagate (no
// rejectWithValue) so the 13001 `code` survives serialization and the UI can
// tell "wrong id" apart from a transient failure.
export const fetchCollectionById = createAsyncThunk(
  "business/fetchCollectionById",
  async (rq: { collectionId: string; includeBusinessDetails?: boolean }) => {
    return await BusinessApiService.getCollectionById(
      rq.collectionId,
      rq.includeBusinessDetails ?? true
    );
  }
);

// A lead's own collections, for the "My Collections" admin screen. Errors
// propagate (no rejectWithValue) so the slice's error/notFound handling stays
// consistent with fetchCollectionById.
export const fetchMyCollections = createAsyncThunk(
  "business/fetchMyCollections",
  async (creatorId: string) => {
    return await BusinessApiService.getMyCollections(creatorId);
  }
);

// createCollection/updateCollection/deleteCollection deliberately let errors
// propagate (no rejectWithValue) — same discipline as fetchCollectionById /
// fetchMyCollections, so a thrown collectionError's `code` (e.g. NOT_FOUND)
// survives Redux Toolkit's miniSerializeError for slice/UI branching.
export const createCollection = createAsyncThunk(
  "business/createCollection",
  async (payload: CreateCollectionPayload) => {
    return await BusinessApiService.createCollection(payload);
  }
);

export const updateCollection = createAsyncThunk(
  "business/updateCollection",
  async (payload: UpdateCollectionPayload) => {
    return await BusinessApiService.updateCollection(payload);
  }
);

export const deleteCollection = createAsyncThunk(
  "business/deleteCollection",
  async (collectionId: string) => {
    await BusinessApiService.deleteCollection(collectionId);
    return collectionId;
  }
);

export const fetchMyBusinesses = createAsyncThunk(
  "business/fetchMyBusinesses",
  async (_: void, { rejectWithValue }) => {
    try {
      return await BusinessApiService.getMyBusinesses();
    } catch (error) {
      return rejectWithValue(String(error));
    }
  }
);

export const fetchBusinessById = createAsyncThunk(
  "business/fetchBusinessById",
  async (businessId: string, { rejectWithValue }) => {
    try {
      return await BusinessApiService.getBusinessById(businessId);
    } catch (error) {
      return rejectWithValue(String(error));
    }
  }
);

export const fetchBusinessListings = createAsyncThunk(
  "business/fetchBusinessListings",
  async (
    { businessId, ignoreIds }: { businessId: string; ignoreIds: string[] },
    { rejectWithValue }
  ) => {
    try {
      // return await BusinessApiService.getBusinessListings(businessId, ignoreIds);
      return await ProductApiService.getProductsByBusinessId(businessId);
    } catch (error) {
      return rejectWithValue(String(error));
    }
  }
);

export const fetchUserBusinesses = createAsyncThunk(
  "business/fetchUserBusinesses",
  async (userId: string, { rejectWithValue }) => {
    try {
      return await BusinessApiService.getBusinessesByUserId(userId);
    } catch (error) {
      return rejectWithValue(String(error));
    }
  }
);

export const createBusiness = createAsyncThunk(
  "business/createBusiness",
  async (payload: CreateBusinessPayload, { rejectWithValue }) => {
    try {
      return await BusinessApiService.createBusiness(payload);
    } catch (error) {
      return rejectWithValue(String(error));
    }
  }
);

export const updateBusiness = createAsyncThunk(
  "business/updateBusiness",
  async (payload: UpdateBusinessPayload, { rejectWithValue }) => {
    try {
      return await BusinessApiService.updateBusiness(payload);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

export const deleteBusiness = createAsyncThunk(
  "business/deleteBusiness",
  async (businessId: string, { rejectWithValue }) => {
    try {
      await BusinessApiService.deleteBusiness(businessId);
      return businessId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

export const updateBusinessMoreDetails = createAsyncThunk(
  "business/updateBusinessMoreDetails",
  async (
    { businessId, moreDetails }: { businessId: string; moreDetails: BusinessMoreDetails },
    { rejectWithValue }
  ) => {
    try {
      const saved = await BusinessApiService.updateBusinessMoreDetails(businessId, moreDetails);
      return { businessId, moreDetails: saved ?? {} };
    } catch (error) {
      return rejectWithValue(String(error));
    }
  }
);

export const fetchBusinessCustomers = createAsyncThunk(
  "business/fetchBusinessCustomers",
  async (businessId: string, { rejectWithValue }) => {
    try {
      return await BusinessApiService.getCustomersForBusiness(businessId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

export const fetchBusinessRatings = createAsyncThunk(
  "business/fetchBusinessRatings",
  async (businessId: string, { rejectWithValue }) => {
    try {
      return await BusinessApiService.getBusinessRatings(businessId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

export const submitBusinessRating = createAsyncThunk(
  "business/submitBusinessRating",
  async (payload: SubmitRatingPayload, { dispatch, rejectWithValue }) => {
    try {
      const saved = await BusinessApiService.submitBusinessRating(payload);
      // Refetch summary so avgRating/totalRatings are accurate (and the
      // new/updated row appears in the list with rated_by populated).
      dispatch(fetchBusinessRatings(payload.businessId));
      return saved;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

export const deleteBusinessRating = createAsyncThunk(
  "business/deleteBusinessRating",
  async (
    { ratingId, businessId }: { ratingId: string; businessId: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await BusinessApiService.deleteBusinessRating(ratingId);
      dispatch(fetchBusinessRatings(businessId));
      return ratingId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

const BusinessThunk = {
  businessSearch,
  collectionSearchByName,
  fetchCollectionById,
  fetchMyCollections,
  createCollection,
  updateCollection,
  deleteCollection,
  fetchMyBusinesses,
  fetchBusinessById,
  fetchBusinessListings,
  fetchUserBusinesses,
  createBusiness,
  updateBusiness,
  deleteBusiness,
  updateBusinessMoreDetails,
  fetchBusinessCustomers,
  fetchBusinessRatings,
  submitBusinessRating,
  deleteBusinessRating,
};

export default BusinessThunk;
